/**
 * INBOX-01 hooks — the shared office inbox over the `message` spine.
 * Conversations come from the v_inbox_conversation view; per-conversation
 * assignment / done / read state lives in conversation_state.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface InboxConversation {
  conversation_key: string;
  custno: string | null;
  endpoint: string | null;
  channel: string;
  direction: string;
  subject: string | null;
  body: string | null;
  last_at: string;
  message_count: number;
  needs_reply: boolean;
  fullname: string | null;
  assignee: string | null;
  conv_status: "open" | "snoozed" | "done" | null;
  last_read_at: string | null;
  unread: boolean;
  /** INBOX-02: every inbound message newer than last_read_at — the honest
   *  count (Podium-comparable), not just "latest message is inbound". */
  unread_count: number;
  /** Provider-side display name (Podium's contact, e.g. "Bill Lindquist") —
   * shown as "texts as Bill Lindquist" when it differs from the ESC name. */
  provider_contact_name: string | null;
  /** Podium conversation uid — present = write-back (Done/Claim → Podium) applies. */
  provider_conversation_id: string | null;
}

export interface InboxMessage {
  id: string;
  custno: string | null;
  channel: string;
  direction: string;
  message_type: string | null;
  endpoint: string | null;
  subject: string | null;
  body: string | null;
  status: string | null;
  created_at: string;
  logged_by: string | null;
  attachments: { name: string }[] | null;
}

/** "Simonson,Seth 1011072" → "Seth Simonson". Unmatched conversations fall
 *  back to the PROVIDER'S contact name (Podium knows "Rick Peterson" even
 *  when no ESC account matches the phone — Seth 2026-06-12: the inbox showed
 *  the raw number where Podium showed the name), then the endpoint. */
export function conversationName(c: {
  fullname: string | null;
  endpoint: string | null;
  custno: string | null;
  provider_contact_name?: string | null;
}): string {
  if (c.fullname) {
    const f = c.fullname.replace(/\s+\d{4,}$/, "");
    const m = f.match(/^([^,]+),(.+)$/);
    return (m ? `${m[2].trim()} ${m[1].trim()}` : f).trim();
  }
  return (
    c.provider_contact_name?.trim() ||
    (c.endpoint ?? c.custno ?? "Unknown sender")
  );
}

export function useInboxConversations() {
  return useQuery({
    queryKey: ["inbox_conversations"],
    staleTime: 20 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<InboxConversation[]> => {
      const { data, error } = await supabase
        .from("v_inbox_conversation")
        .select("*")
        .order("last_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as InboxConversation[];
    },
  });
}

export function useConversationMessages(
  custno: string | null,
  endpoint: string | null,
) {
  return useQuery({
    queryKey: ["conversation_messages", custno, endpoint],
    enabled: Boolean(custno || endpoint),
    staleTime: 15 * 1000,
    queryFn: async (): Promise<InboxMessage[]> => {
      let q = supabase
        .from("message")
        .select(
          "id, custno, channel, direction, message_type, endpoint, subject, body, status, created_at, logged_by, attachments",
        )
        .is("deleted_at", null) // soft-deleted messages are hidden (Seth 2026-06-26)
        .order("created_at", { ascending: true });
      if (custno) q = q.eq("custno", custno);
      else if (endpoint) q = q.is("custno", null).ilike("endpoint", endpoint);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as InboxMessage[];
    },
  });
}

/** Reversible per-message cleanup (Seth 2026-06-26) — hide/restore a message in
 *  the Pegasus thread + inbox (doesn't touch Podium). Refreshes both views. */
export function useSetMessageDeleted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, deleted }: { id: string; deleted: boolean }) => {
      const { error } = await supabase.rpc("set_message_deleted", { p_id: id, p_deleted: deleted });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversation_messages"] });
      qc.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}

export function useCurrentUserEmail() {
  return useQuery({
    queryKey: ["me_email"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data?.user?.email ?? null;
    },
  });
}

export function useUpdateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      conversation_key: string;
      custno: string | null;
      endpoint: string | null;
      patch: Record<string, unknown>;
      /** Podium conversation uid — when present, Done/Reopen/Claim/Unassign
       * also WRITE BACK to Podium so the two inboxes agree (the "vice versa").
       * Fire-and-surface: the local update never blocks on Podium; a write-back
       * failure is reported, and the 1-min reconcile would re-mirror Podium's
       * state anyway (so the two can't drift silently). */
      provider_conversation_id?: string | null;
    }) => {
      // INBOX-REOPEN-01: a Done conversation reopens by itself when the
      // customer writes again (the view compares inbound times against
      // closed_at). Stamp closed_at on Done, clear it on Reopen; the DB
      // trigger is the safety net for writes that don't come through here.
      const closed =
        v.patch.status === "done"
          ? { closed_at: new Date().toISOString() }
          : v.patch.status === "open"
            ? { closed_at: null }
            : {};
      const { error } = await supabase.from("conversation_state").upsert(
        {
          conversation_key: v.conversation_key,
          custno: v.custno,
          endpoint: v.endpoint,
          ...v.patch,
          ...closed,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "conversation_key" },
      );
      if (error) throw error;

      // NOTIFY — tell the new assignee, unless they claimed it themselves.
      if ("assignee" in v.patch && v.patch.assignee) {
        const { data: u } = await supabase.auth.getUser();
        const me = u.user?.email ?? null;
        const assignee = v.patch.assignee as string;
        if (assignee && assignee !== me) {
          await supabase.from("notification").insert({
            recipient: assignee,
            type: "assigned",
            conversation_key: v.conversation_key,
            custno: v.custno,
            title: "A conversation was assigned to you",
            actor: me,
          });
        }
      }

      if (v.provider_conversation_id) {
        const body: Record<string, unknown> = {
          conversation_uid: v.provider_conversation_id,
        };
        if (v.patch.status === "done") body.closed = true;
        if (v.patch.status === "open") body.closed = false;
        if ("assignee" in v.patch) body.assignee_email = v.patch.assignee ?? null;
        if ("closed" in body || "assignee_email" in body) {
          const { data, error: fnError } = await supabase.functions.invoke(
            "podium-update",
            { body },
          );
          const failed =
            fnError || (data && typeof data === "object" && (data as { ok?: boolean }).ok === false);
          if (failed) {
            throw new Error(
              "Saved in Pegasus, but Podium didn't take the update — it may show differently there until it's retried.",
            );
          }
        }
      }
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["inbox_conversations"] }),
  });
}
